/**
 * Copyright Highway9 Networks Inc.
 */

import { useAppSelector } from '~/store/hooks';
import { authUser, selectActiveRoleId } from '~/store/context/authSlice';
import { useRoles } from './useRoles';
import type { Role } from '~/services/user-service';
import { authenticationService, userService, PARTNER_ROLES, TENANT_ROLES, SYSTEM_ENGINEER_ROLES, OPERATOR_ROLES, PLANNING_MAP_EXCLUDED_ROLES, ROLE_NAMES, type RoleName } from '~/services';
import type { UserInfo } from '~/types/user';

export type CurrentRoleInfo = {
  /** The resolved logged-in user record (Redux, falling back to localStorage during the boot-time hydration gap), or null if not logged in. */
  user: UserInfo | null;
  /** Effective role(s) used for permissions today, as reported by the backend (post RAN_KPI_ONLY rewrite, if any). */
  effectiveRoles: string[];
  /** The operator Role Switcher's active override, resolved to a full Role, or null when not overridden. */
  overrideRole: Role | null;
  /** True when `activeRoles` is RAN_KPI_ONLY (MCS-3799 partner KPI-only user, or a Role Mask override selecting that role). Equivalent to the legacy `isPartnerKpiOnlyUser()`. */
  isKpiOnly: boolean;
  /** True when acting as an operator: the session flag when unmasked, or the masked role's name when the Role Switcher is overridden. Equivalent to the legacy `authenticationService.isOperator()` when not masked. */
  isOperator: boolean;
  /** True when `activeRoles` contains any partner-tier role. Equivalent to the legacy `authenticationService.isPartner()`. */
  isPartner: boolean;
  /** True when `activeRoles` contains any tenant-tier role. Equivalent to the legacy `authenticationService.isTenant()`. */
  isTenant: boolean;
  /** True when `activeRoles` contains any system-engineer-tier role. Equivalent to the legacy `authenticationService.isSystemEngineer()`. */
  isSystemEngineer: boolean;
  /** True when `activeRoles` includes TENANT_ADMIN. Equivalent to the legacy `authenticationService.isTenantAdmin()`. */
  isTenantAdmin: boolean;
  /** True when `activeRoles` includes MOBILE_APP_READONLY (radio installer, mobile-app-only access). */
  isRadioInstaller: boolean;
  /** CI-260: Planning Map tab is shown to every role except the read-only/auditor roles. Equivalent to the legacy `authenticationService.canViewPlanningMap()`. */
  canViewPlanningMap: boolean;
};

/**
 * Reports the logged-in user's role identity across both override mechanisms in this app:
 * the backend-driven RAN_KPI_ONLY roles/displayRole rewrite (MCS-3799), and the operator-only
 * Role Switcher permission mask (`activeRoleId` in authSlice).
 *
 * `effectiveRoles` is always the true, unmasked identity — the raw `roles` field the backend
 * uses for permissions today, never affected by the Role Switcher. For "is this account
 * actually an operator" checks (e.g. `isTrueOperator` in `TopBar.tsx`, which gates the Role
 * Switcher's own visibility), use `effectiveRoles` directly.
 *
 * Every `is*`/`canViewPlanningMap` boolean, by contrast, is masked-aware: when the Role
 * Switcher is overridden it reflects the override's role instead of the account's real one,
 * so a masked operator experiences the full UI effect of the selected role (nav visibility,
 * hidden fields, feature gating), not just its CASL permissions. Most of them do this by
 * reading `activeRoles` (the override's role name, or `effectiveRoles` when unmasked) —
 * `isOperator` is the one exception: when unmasked it reads the backend session flag
 * (`authenticationService.isOperator()`) rather than `activeRoles`, to preserve its legacy
 * semantics (see its own doc below). Gating actions should still prefer `useCan`/
 * `useSubjectPermissions` from `~/rbac` where possible; these booleans exist for the legacy
 * call sites that predate CASL.
 */
export function useCurrentRole(): CurrentRoleInfo {
  // Redux `authUser` is null until `onAppStart` finishes its user-info fetch (a network
  // round-trip). On reload/new-tab with an existing session, localStorage already has the
  // right answer synchronously — fall back to it so callers don't see a flash of "no roles"
  // (same pattern as `TopBar.tsx`'s `currentUserInfo`).
  const user = useAppSelector(authUser) ?? userService.currentUserInfo;
  const activeRoleId = useAppSelector(selectActiveRoleId);
  const { getRoleById } = useRoles();

  const effectiveRoles = user?.roles ?? [];

  const overrideRole = activeRoleId ? getRoleById(activeRoleId) ?? null : null;

  // Masked role when the Role Switcher is overridden, otherwise the account's real roles.
  // Every is*/canViewPlanningMap boolean below is derived from this, not effectiveRoles.
  const activeRoles = overrideRole ? [overrideRole.name] : effectiveRoles;

  const isKpiOnly = activeRoles.includes(ROLE_NAMES.RAN_KPI_ONLY);
  const isOperator = overrideRole ? OPERATOR_ROLES.includes(overrideRole.name as RoleName) : authenticationService.isOperator();
  const isPartner = activeRoles.some((role) => PARTNER_ROLES.includes(role as RoleName));
  const isTenant = activeRoles.some((role) => TENANT_ROLES.includes(role as RoleName));
  const isSystemEngineer = activeRoles.some((role) => SYSTEM_ENGINEER_ROLES.includes(role as RoleName));
  const isTenantAdmin = activeRoles.includes(ROLE_NAMES.TENANT_ADMIN);
  const isRadioInstaller = activeRoles.includes(ROLE_NAMES.MOBILE_APP_READONLY);
  const canViewPlanningMap = isOperator || (activeRoles.length > 0 && !activeRoles.some((role) => PLANNING_MAP_EXCLUDED_ROLES.includes(role as RoleName)));

  return {
    user, effectiveRoles, overrideRole, isKpiOnly,
    isOperator, isPartner, isTenant, isSystemEngineer, isTenantAdmin, isRadioInstaller, canViewPlanningMap,
  };
}
